import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import useDebounce from "../../../hooks/useDebounce";
import messages from "../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import {
  deleteCompanyUserRequest,
  getCompanyUsersRequest,
} from "../../../store/CompanyPortal/Employees/actions";
import {
  UIButton,
  UIPaginationBar,
} from "../../../components/ui";
import TeamToolbar from "./TeamToolbar";
import EmployeesTable from "./EmployeesTable";
import DeleteEmployeeModal from "./DeleteEmployeeModal";
import TeamListEmptyState from "./TeamListEmptyState";
import { buildCompanyUsersListParams } from "./employeeMappers";
import { companyPermissionModules } from "../../../utils/Constants";
import {
  canAddCompanyModule,
  canDeleteCompanyModule,
  canEditCompanyModule,
} from "../../../utils/Permissions";
import { UI_TABLE_DEFAULT_PAGE_SIZE } from "../../../utils/tablePageSize";

const EMPLOYEES_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const SEARCH_DEBOUNCE_MS = 300;

const EmployeesManagement = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const lang = useSelector((state) => state.locale.lang);
  const rows = useSelector((state) => state.companyEmployees.list);
  const meta = useSelector((state) => state.companyEmployees.meta);
  const deleting = useSelector((state) => state.companyEmployees.deleting);
  const login = useSelector((state) => state.auth.login);
  const copy = messages[lang].teamManagement;
  const employeesCopy = copy.employees;
  const employeesModule = companyPermissionModules.employees;
  const canAdd = canAddCompanyModule(employeesModule, login);
  const canEdit = canEditCompanyModule(employeesModule, login);
  const canDelete = canDeleteCompanyModule(employeesModule, login);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(UI_TABLE_DEFAULT_PAGE_SIZE);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);

  const listParams = useMemo(
    () =>
      buildCompanyUsersListParams({
        search: debouncedSearch,
        page,
        pageSize,
      }),
    [debouncedSearch, page, pageSize],
  );

  useEffect(() => {
    dispatch(getCompanyUsersRequest(listParams));
  }, [dispatch, lang, listParams]);

  const total = meta.count || 0;
  const pages = meta.pages || Math.max(Math.ceil(total / pageSize), 1);
  const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
  };

  const handlePageSizeChange = (nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  };

  const handleAddEmployee = () => {
    history.push(ROUTE_PATHS.companyTeamEmployeeCreate);
  };

  const handleViewEmployee = (employee) => {
    if (!employee?.id) return;
    history.push(
      ROUTE_PATHS.companyTeamEmployeeView.replace(":id", employee.id),
    );
  };

  const handleEditEmployee = (employee) => {
    if (!employee?.id) return;
    history.push({
      pathname: ROUTE_PATHS.companyTeamEmployeeView.replace(":id", employee.id),
      state: { startInEdit: true },
    });
  };

  const handleRemove = (employee) => {
    setEmployeeToDelete(employee);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setEmployeeToDelete(null);
  };

  const confirmDeleteEmployee = () => {
    if (!employeeToDelete || deleting) return;
    dispatch(
      deleteCompanyUserRequest({
        id: employeeToDelete.id,
        onSuccess: () => {
          setEmployeeToDelete(null);
          dispatch(getCompanyUsersRequest(listParams));
        },
      }),
    );
  };

  return (
    <Box className="team-management__panel">
      <TeamToolbar
        search={search}
        searchPlaceholder={employeesCopy.searchPlaceholder}
        onSearchChange={handleSearchChange}
      >
        {canAdd && (
          <UIButton
            variant="outlined"
            size="large"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddEmployee}
            className="team-management__add"
          >
            {employeesCopy.addEmployee}
          </UIButton>
        )}
      </TeamToolbar>

      {rows.length ? (
        <>
          <EmployeesTable
            rows={rows}
            pageSize={pageSize}
            labels={employeesCopy.table}
            onView={handleViewEmployee}
            onEdit={handleEditEmployee}
            onRemove={handleRemove}
            canEdit={canEdit}
            canDelete={canDelete}
          />
          <UIPaginationBar
            currentPage={page}
            pages={pages}
            pageSize={pageSize}
            pageSizeOptions={EMPLOYEES_PAGE_SIZE_OPTIONS}
            rangeTemplate={employeesCopy.rangeTemplate}
            rangeFrom={rangeFrom}
            rangeTo={rangeTo}
            rangeTotal={total}
            rowsPerPageLabel={copy.rowsPerPageLabel}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      ) : (
        <TeamListEmptyState
          searchTerm={debouncedSearch}
          total={total}
          copy={employeesCopy}
          canAdd={canAdd}
          actionLabel={employeesCopy.addEmployee}
          onAction={handleAddEmployee}
        />
      )}

      <DeleteEmployeeModal
        open={Boolean(employeeToDelete)}
        employee={employeeToDelete}
        copy={employeesCopy.deleteModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteEmployee}
      />
    </Box>
  );
};

export default EmployeesManagement;
